import { useMemo, useState } from "react";
import { Grid2, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTranslate } from "../../../../../hooks/useTranslate";
import { useHomeSchoolForm } from "../../hooks/useHomeSchoolForm";
import { useGetReasonsForJoining } from "../../../../../hooks/useGetReasonsForJoining";
import TextInput from "../../../../../components/TextInput";
import FileDropUploader from "../../../../../components/FileDropUploader";
import DatePickerInput from "../../../../../components/DatePickerInput";
import Preloader from "../../../../../components/Preloader";
import Select from "../../../../../components/Select";
import "./HomeSchoolForm.css";
import { isVersionUK, isVersionUS } from "../../../../../services/versionService";

const HomeSchoolForm = (props) => {
    const {
        data,
        isSaving,
        onSave,
        isDisabled,
    } = props;

    const { t } = useTranslate();
    const { reasonsOptions } = useGetReasonsForJoining();

    const [transcripts, setTranscripts] = useState([]);

    const {
        register, control, errors,
        handleSubmit,
    } = useHomeSchoolForm(data);

    const uploadedFiles = useMemo(() => (data?.transcript ? [data.transcript] : null), [data.transcript]);

    const onSubmit = handleSubmit((formData) => {
        onSave?.({ ...formData, transcripts });
    });
    const onChangeTranscripts = (files) => {
        setTranscripts(files);
    };

    return (
        <Preloader isLoading={isSaving} overflow showInitiallyOverflow>
            <form className="home-school-form" onSubmit={onSubmit}>
                <Typography variant="h4" component="div" className="text-primary">
                    {t("previousEducation.form.previousHomeInstitutionDetails")}
                </Typography>
                <Typography variant="body1" component="div" className="mt-4">
                    {t("previousEducation.form.previousHomeInstitutionDetailsSubtitle")}
                </Typography>
                <Grid2 container spacing={4} className="mt-4">
                    <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                        <TextInput
                            label={t("previousEducation.form.teacherName")}
                            register={register}
                            name="homeSchoolTeacherName"
                            error={errors.homeSchoolTeacherName?.message}
                            disabled={isDisabled}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                        <TextInput
                            label={t("previousEducation.form.teacherRelationship")}
                            register={register}
                            name="homeSchoolTeacherRelationship"
                            error={errors.homeSchoolTeacherRelationship?.message}
                            disabled={isDisabled}
                        />
                    </Grid2>
                </Grid2>
                <Grid2 container spacing={4}>
                    <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                        <TextInput
                            label={t("previousEducation.form.teacherEmail")}
                            register={register}
                            name="homeSchoolTeacherEmail"
                            error={errors.homeSchoolTeacherEmail?.message}
                            disabled={isDisabled}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 6 }} className="mb-4">
                        <TextInput
                            label={t("previousEducation.form.teacherPhone")}
                            register={register}
                            name="homeSchoolTeacherPhone"
                            error={errors.homeSchoolTeacherPhone?.message}
                            disabled={isDisabled}
                        />
                    </Grid2>
                </Grid2>
                <Grid2 size={12} className="mb-4">
                    <TextInput
                        label={t("previousEducation.form.subjectsStudied")}
                        register={register}
                        name="homeSchoolSubjectsStudied"
                        error={errors.homeSchoolSubjectsStudied?.message}
                        disabled={isDisabled}
                        multiline
                    />
                </Grid2>
                <Grid2 size={12} className="mb-4">
                    <TextInput
                        label={t("previousEducation.form.assessmentMethods")}
                        register={register}
                        name="homeSchoolAssessmentMethods"
                        error={errors.homeSchoolAssessmentMethods?.message}
                        disabled={isDisabled}
                        multiline
                    />
                </Grid2>
                <Grid2 size={12} className="mb-4">
                    <TextInput
                        label={t("previousEducation.form.previousExperience")}
                        register={register}
                        name="homeSchoolPreviousEducationExperience"
                        error={errors.homeSchoolPreviousEducationExperience?.message}
                        disabled={isDisabled}
                        multiline
                    />
                </Grid2>
                <Grid2 size={12} className="mb-4">
                    <TextInput
                        label={t("previousEducation.form.extracurricularActivities")}
                        register={register}
                        name="homeSchoolExtracurricularActivities"
                        error={errors.homeSchoolExtracurricularActivities?.message}
                        disabled={isDisabled}
                        multiline
                    />
                </Grid2>
                <Grid2 size={12} className="mb-4">
                    <TextInput
                        label={t("previousEducation.form.additional")}
                        register={register}
                        name="homeSchoolAdditionalInformation"
                        error={errors.homeSchoolAdditionalInformation?.message}
                        disabled={isDisabled}
                        multiline
                    />
                </Grid2>
                <Grid2 container spacing={4}>
                    <Grid2 size={{ xs: 12, md: 3 }} className="mb-4">
                        <DatePickerInput
                            label={t("previousEducation.form.firstDate")}
                            control={control}
                            name="previousInstitutionFrom"
                            error={errors.previousInstitutionFrom?.message}
                            disabled={isDisabled}
                            disableFuture
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }} className="mb-4">
                        <DatePickerInput
                            label={t("previousEducation.form.lastDate")}
                            control={control}
                            name="previousInstitutionTo"
                            error={errors.previousInstitutionTo?.message}
                            disabled={isDisabled}
                            disableFuture
                        />
                    </Grid2>
                    {isVersionUK && (
                        <Grid2 size={{ xs: 12, md: 6 }} className="mb-8">
                            <Select
                                label={t("previousEducation.form.joinReason")}
                                register={register}
                                control={control}
                                name="reasonForJoining"
                                error={errors.reasonForJoining?.message}
                                disabled={isDisabled}
                                options={[{ value: "", label: t("previousEducation.form.selectReason"), disabled: true }, ...reasonsOptions]}
                            />
                        </Grid2>
                    )}
                </Grid2>
                <FileDropUploader
                    uploadTextElement={(
                        <>
                            <Typography variant="h5" component="div" className="text-primary">
                                {t("previousEducation.form.pleaseAttachDocument")}
                            </Typography>
                            <Typography variant="body1" component="div" className="mt-4">
                                {t("previousEducation.form.documentsAccepts")}
                            </Typography>
                        </>
                    )}
                    accept={{
                        "image/jpeg": [".png", ".jpg", ".jpeg"],
                        "application/pdf": [".pdf"],
                        "application/word": [".doc", ".docx", ".odt", ".rtf", ".txt"],
                    }}
                    uploadedFiles={uploadedFiles}
                    showFiles
                    onChange={onChangeTranscripts}
                    disabled={isDisabled}
                />
                <Button
                    variant="contained"
                    className="button-accent mt-8 w-full"
                    type="submit"
                    loading={isSaving}
                    disabled={isDisabled}
                >
                    {t("previousEducation.form.submit")}
                </Button>
            </form>
        </Preloader>
    );
};

export default HomeSchoolForm;
